import { useEffect, useRef } from "react";
import gsap from "gsap";
import Spinner from "../../../shared/components/ui/Spinner";

export default function DetectionStatsSection({ detectionStats, showRain }) {
  const cardsRef = useRef([]);

  useEffect(() => {
    if (detectionStats && cardsRef.current.length) {
      gsap.fromTo(
        cardsRef.current.filter(Boolean),
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.4, stagger: 0.05, ease: "power2.out" }
      );
    }
  }, [detectionStats]);

  if (!detectionStats) {
    return (
      <div className="stats-loading-section">
        <Spinner size="md" />
        Loading detection statistics...
      </div>
    );
  }

  const {
    processed,
    with_detections,
    player_distribution,
    crop_types,
    avg_detection_confidence,
    recrop_count,
    recrop_rate,
    roll_stats,
  } = detectionStats;

  return (
    <div className={`detection-stats-grid ${showRain ? "matrix-grid" : ""}`}>
      {/* Summary row */}
      <div className="stat-cards-grid">
        <div className="stat-card total" ref={(el) => (cardsRef.current[0] = el)}>
          <span className="stat-value">{processed || 0}</span>
          <span className="stat-label">Images Analyzed</span>
        </div>
        <div className="stat-card done" ref={(el) => (cardsRef.current[1] = el)}>
          <span className="stat-value">{with_detections || 0}</span>
          <span className="stat-label">With Detections</span>
        </div>
        <div className="stat-card info" ref={(el) => (cardsRef.current[2] = el)}>
          <span className="stat-value">{avg_detection_confidence?.toFixed(2) || "-"}</span>
          <span className="stat-label">Avg Confidence</span>
        </div>
        <div className="stat-card warning" ref={(el) => (cardsRef.current[3] = el)}>
          <span className="stat-value">{recrop_count || 0}</span>
          <span className="stat-label">Manual Adjustments</span>
        </div>
        <div className="stat-card highlight" ref={(el) => (cardsRef.current[4] = el)}>
          <span className="stat-value">{((recrop_rate || 0) * 100).toFixed(1)}%</span>
          <span className="stat-label">Recrop Rate</span>
        </div>
      </div>

      {/* Charts grid */}
      <div className="detection-charts">
        {/* Player count distribution */}
        <div className="detection-chart">
          <h4>Players Per Image</h4>
          {player_distribution?.length > 0 ? (
            <div className="chart-bars">
              {player_distribution.slice(0, 15).map((item) => {
                const maxCount = Math.max(...player_distribution.map((d) => d.count));
                const pct = (item.count / maxCount) * 100;
                return (
                  <div key={item.name} className="chart-bar-row">
                    <div className="bar-label">{item.name} players</div>
                    <div className="bar-track">
                      <div
                        className="bar-fill"
                        style={{
                          width: `${pct}%`,
                          background: showRain
                            ? "linear-gradient(90deg, #8ce5ff, #8ce5ff88)"
                            : "#8ce5ff",
                        }}
                      />
                    </div>
                    <div className="bar-count">{item.count}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-data">No data available</div>
          )}
        </div>

        {/* Crop types */}
        <div className="detection-chart">
          <h4>Crop Types Used</h4>
          {crop_types?.length > 0 ? (
            <div className="chart-bars">
              {crop_types.map((item) => {
                const maxCount = Math.max(...crop_types.map((d) => d.count));
                const pct = (item.count / maxCount) * 100;
                return (
                  <div key={item.name} className="chart-bar-row">
                    <div className="bar-label">{item.name}</div>
                    <div className="bar-track">
                      <div
                        className="bar-fill"
                        style={{
                          width: `${pct}%`,
                          background: showRain
                            ? "linear-gradient(90deg, #a29bff, #a29bff88)"
                            : "#a29bff",
                        }}
                      />
                    </div>
                    <div className="bar-count">{item.count}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-data">No data available</div>
          )}
        </div>

        {/* Roll/leveling stats */}
        <div className="detection-chart roll-stats">
          <h4>Auto-Level (Roll) Statistics</h4>
          <div className="roll-stats-grid">
            <div className="roll-stat">
              <span className="roll-value">{roll_stats?.avg?.toFixed(2) || "-"}deg</span>
              <span className="roll-label">Average Correction</span>
            </div>
            <div className="roll-stat">
              <span className="roll-value">{roll_stats?.p50?.toFixed(2) || "-"}deg</span>
              <span className="roll-label">Median (P50)</span>
            </div>
            <div className="roll-stat">
              <span className="roll-value">{roll_stats?.p90?.toFixed(2) || "-"}deg</span>
              <span className="roll-label">P90</span>
            </div>
            <div className="roll-stat">
              <span className="roll-value">{roll_stats?.max?.toFixed(2) || "-"}deg</span>
              <span className="roll-label">Max Correction</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
